import React from "react"
import { Link, graphql } from "gatsby"
import Layout from "../components/layout"
import SEO from "../components/seo"
import Social from '../components/social'

const BlogIndex = ({ data, location }) => {
  const siteTitle = data.site.siteMetadata.title

  return (
    <Layout location={location} title={siteTitle}>
      <SEO
        title="Lenvin Gonsalves -- Software Engineer"
        description="Software Engineer at Fleek (YC W22). Building production multi-agent AI systems, MCP architecture, and full-stack product infrastructure. Previously built Makro Pro (Thailand's #1 grocery e-commerce) and the Wild Planet Alexa skill."
        pathname="/landing"
      />
      <h1>Hey, I'm Lenvin</h1>
      <p>
        I'm a Software Engineer at <a href='https://joinfleek.com/'>Fleek</a> (YC W22), where I build production AI systems -- multi-agent pipelines, internal developer platforms, and full-stack product infrastructure.
      </p>
      <p>Over 6 years I've shipped across the full stack:</p>
      <ul>
          <li>Multi-agent AI systems with <b>LangGraph</b>, <b>LangChain</b>, OpenAI &amp; Claude</li>
          <li>Internal developer platforms using <b>MCP architecture</b> (Model Context Protocol)</li>
          <li>Serverless backend with <b>TypeScript</b>, <b>Node.js</b>, <b>AWS Lambda</b>, <b>SQS/SNS</b>, <b>SST</b></li>
          <li>APIs with <b>GraphQL</b> (Apollo Server), <b>PostgreSQL</b> (Kysely), <b>Redis</b>, <b>OpenSearch</b></li>
          <li>React Native mobile apps (two production apps) and <b>Next.js</b> web</li>
          <li>AI grading pipelines using <b>vector embeddings</b> and cosine similarity</li>
          <li>Contributed to <b>Node.js core</b> -- automated ICU timezone updates via GitHub Actions</li>
      </ul>
      <p>I've also contributed to open source:</p>
      <ul>
        <li><a href='https://github.com/nodejs/node'>Node.js</a> -- ICU timezone automation (merged)</li>
        <li><a href='https://www.gatsbyjs.com/plugins/gatsby-remark-spellcheck/'>Gatsby</a> -- published gatsby-remark-spellcheck plugin</li>
      </ul>

      <hr/>
      <h2>Experience</h2>

      <h3>Software Engineer -- <a href='https://joinfleek.com/'>Fleek</a> (YC W22) <span style={{fontWeight: 'normal', fontSize: '0.85em'}}>Feb 2023 - Present · Bengaluru</span></h3>
      <p>
        Full-stack engineer at a B2B wholesale marketplace for secondhand fashion (Series A, backed by a16z, HV Capital). Joined just after seed round as one of the first Bangalore engineers.
      </p>
      <ul>
        <li>Designed and built <b>be-agents</b> -- a production multi-agent AI system using LangGraph with broker routing, ReAct agents, and memory/retrieval pipelines</li>
        <li>Built <b>FleekOS</b> -- an internal developer platform using <b>MCP architecture</b>, letting business teams spin up internal tools connected to production databases without engineering tickets</li>
        <li>Shipped <b>FleekSort</b> -- AI-powered clothing grading pipeline at 95% accuracy using vector embeddings and cosine similarity</li>
        <li>Built two production <b>React Native</b> mobile apps (buyer + supplier) from scratch in a monorepo</li>
        <li>Owned serverless infrastructure on <b>AWS Lambda, SQS/SNS, PostgreSQL, Redis, OpenSearch</b> via SST/CDK</li>
        <li>Led supply pod of 4-5 engineers; authored RFCs, ERDs, and architectural docs for major systems</li>
      </ul>

      <h3>Software Engineer -- <a href='https://oozou.com/'>OOZOU</a> <span style={{fontWeight: 'normal', fontSize: '0.85em'}}>May 2021 - Jan 2023 · Bangkok</span></h3>
      <p>
        OOZOU is a premier product design and engineering agency based in Bangkok, building for clients across Southeast Asia.
      </p>
      <p>
        Embedded with <b>CP Axtra (Siam Makro)</b> to build <a href='https://www.makro.pro/en'><b>Makro Pro</b></a> -- Thailand's B2B wholesale e-commerce platform. Joined as one of the first 10 engineers; team scaled to 100+ over two years. Took the platform from zero to <b>฿1M+ revenue per day</b>. Today Makro Pro is the <b>#1 grocery e-commerce platform in Thailand</b> with 5,000+ daily orders and $300M+ GMV/year.
      </p>
      <ul>
        <li>Built REST &amp; <b>GraphQL APIs</b> orchestrating complex fulfillment flows across 100+ Makro stores</li>
        <li>Developed frontend interfaces in <b>TypeScript, NestJS, jQuery</b> and <b>Shopify Liquid</b></li>
        <li>Owned end-to-end features for a marketplace serving thousands of business customers daily</li>
        <li>Went from individual contributor to enabling colleagues -- improving team velocity through shared patterns and tooling</li>
      </ul>

      <h3>Software Engineer -- <a href='https://web.archive.org/web/2021/https://oykuapp.com/'>OYKU</a> <span style={{fontWeight: 'normal', fontSize: '0.85em'}}>Jun 2019 - Apr 2021 · Bengaluru</span></h3>
      <p>
        Early employee at OYKU, a no-code platform that let podcasters, DJs, and YouTubers publish their own streaming apps to iOS, Android, and Alexa -- in under 30 minutes. Part of <b>Pioneer accelerator</b>; called for a <b>Y Combinator</b> interview.
      </p>
      <ul>
        <li>Built <b>Wild Planet</b> -- an Alexa skill for animal sounds that hit <b>43,000+ daily requests</b> in India in 2020</li>
        <li>Developed the no-code creator hub frontend in <b>React</b> and backend microservices in <b>Node.js + MongoDB</b></li>
        <li>Built SEO-friendly marketing site in <b>Gatsby</b></li>
        <li>Worked across web, mobile, and voice (Alexa) -- often as the only or one of two engineers</li>
      </ul>

      <hr/>
      <p>
        I write occasionally about systems I've built. Some posts are <Link to='/posts'>here</Link>, also on <a href='https://dev.to/98lenvi'>Dev.to</a>.
      </p>
      <p>
        <Social/>
      </p>
      <div className='spacer'/>
    </Layout>
  )
}

export default BlogIndex

export const pageQuery = graphql`
  query {
    site {
      siteMetadata {
        title
      }
    }
    allMarkdownRemark(sort: { fields: [frontmatter___date], order: DESC }) {
      edges {
        node {
          excerpt
          fields {
            slug
          }
          frontmatter {
            date(formatString: "MMMM DD, YYYY")
            title
            description
          }
        }
      }
    }
  }
`
